import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function ChiesiPatientFirstPage() {
  useSEO({
    title: 'Chiesi Patient First Clinical Trials | Design For',
    description: 'Designing patient first clinical trials through a patient app for a global pharmaceutical company. Improved medication adherence rates to 85% and reduced time burden by 40%.',
    keywords: 'patient-centric clinical trials, healthcare app design, pharmaceutical research, patient engagement, digital health',
    canonical: '/work/chiesi-patient-first-clinical-trials',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Designing patient first clinical trials through a patient app',
    description: 'We partnered with Chiesi to develop a patient-centric mobile application that transforms the clinical trial experience. The app enables patients to easily track symptoms, medication adherence, and quality of life metrics while participating in respiratory disease research.',
    url: 'https://example.com/work/chiesi-patient-first-clinical-trials',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Global pharmaceutical company - Clinical trial patient engagement',
    keywords: 'patient-centric clinical trials, healthcare app design, pharmaceutical research, patient engagement, digital health, respiratory disease',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Designing patient first clinical trials through a patient app
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Global pharmaceutical company</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                The global pharmco. needed to respond to the increasingly complex needs of patients participating in clinical trials, while also driving efficiency, insight, and cost savings across R&D. The ambition was bigger than a trial tool: to create a patient first digital experience that could support people beyond a single study and inform better decision making across the organisation.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                Using deep patient research and a rigorous experience led approach, we defined a future state vision and technology roadmap that aligned patient needs with clinical, regulatory, and operational realities. This work set a clear foundation for long term capability, not just a single solution.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Defined patient first experience principles for clinical trials',
                  'Created a future state patient experience map and technology roadmap',
                  'Aligned R&D and Patient Engagement teams around a shared digital vision',
                  'Led a rigorous end to end technology vendor selection process via a RFI',
                  'Enabled evidence based decisions using patient research and archetypes',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg"
                  alt="Chiesi Patient First mobile app interface"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_4fbaf406019d9c7c20abec04c65d2b04_1d69a59f.jpg"
                  alt="Clinical trial patient engagement screens"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
            </div>
          </div>

          {/* Footnote */}
          <p className="mt-12 text-xs text-gray-400 italic">
            This work was delivered leading a consulting team during a previous role.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="chiesi-patient-first-clinical-trials" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
